// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Grid2, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseStatus, Loading } from '~/components/common';
// Others
import { GRAY_200 } from '~/utils/constants/color';
import { IUserDetail } from '~/utils/interface/user';
import { getDetailUser } from '~/thunks/user/userThunk';
import { DateFormatEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { formatDate, formatPhoneNumber } from '~/utils/helper/common';
// Styles, images, icons
import styles from './UserInfoTab.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const UserInfoTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/users']);
  const dispatch = useAppDispatch();
  const { id: userId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detailUser, setDetailUser] = useState<IUserDetail>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!userId) return;
    handleGetDetailUser(userId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailUser = (userId: string) => {
    setIsLoading(true);

    dispatch(getDetailUser(userId))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        setDetailUser(data);
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderItemDetailUser = (title: string, children: React.ReactNode) => {
    return (
      <Grid2
        container
        direction={'row'}
        alignItems={'center'}
        justifyContent={'center'}
        borderBottom={1}
        borderColor={GRAY_200}
        height={50}
      >
        <Grid2 size={3}>
          <Typography className={cx('textLabel')}>{title}</Typography>
        </Grid2>

        <Grid2 size={9}>{children}</Grid2>
      </Grid2>
    );
  };
  //#endregion Handle Function

  return (
    <Box id='userInfoTabComponent' className={cx('container')}>
      <Grid2 container direction={'row'} spacing={6} marginTop={'18px'}>
        <Grid2 size={6}>
          <Typography className={cx('textTitle')}>{t('user_detail_title_user_information')}</Typography>

          {renderItemDetailUser(
            t('user_detail_title_status'),
            <Typography className={cx('textValue')}>
              {detailUser?.status ? <BaseStatus label={detailUser?.status} /> : EMPTY_VALUE}
            </Typography>
          )}

          {renderItemDetailUser(
            t('user_detail_title_email'),
            <Typography className={cx('textValue')}>{detailUser?.email || EMPTY_VALUE}</Typography>
          )}

          {renderItemDetailUser(
            t('user_detail_title_phone'),
            <Typography className={cx('textValue')}>
              {detailUser?.phoneNumber ? formatPhoneNumber(detailUser?.phoneNumber || EMPTY_VALUE) : EMPTY_VALUE}
            </Typography>
          )}

          {renderItemDetailUser(
            t('user_detail_title_address'),
            <Typography className={cx('textValue')}>{detailUser?.address || EMPTY_VALUE}</Typography>
          )}
        </Grid2>

        <Grid2 size={6}>
          <Typography className={cx('textTitle')}>{t('user_detail_title_general_information')}</Typography>

          {renderItemDetailUser(
            t('user_detail_title_role'),
            <Typography className={cx('textValue')}>
              {detailUser?.role?.map((item) => item?.name || EMPTY_STRING).join(', ')}
            </Typography>
          )}

          {renderItemDetailUser(
            t('user_detail_title_time_zone'),
            <Typography className={cx('textValue')}>{detailUser?.timezone || EMPTY_VALUE}</Typography>
          )}

          {renderItemDetailUser(
            t('user_detail_title_create_at'),
            <Typography className={cx('textValue')}>
              {detailUser?.createdAt ? formatDate(detailUser?.createdAt, DateFormatEnum.MMM_D_YYYY) : EMPTY_VALUE}
            </Typography>
          )}
        </Grid2>
      </Grid2>

      {isLoading && <Loading />}
    </Box>
  );
};

export default UserInfoTab;
